import * as React from 'react';

import { Res } from 'core/api';
import { Binding } from 'core/infrastructure';

import * as Format from './Format';
import { Row, Column } from './Grid';
import * as _ComboBox from './ComboBox';
import * as _TextBox from './TextBox';
import * as _DecimalBox from './DecimalBox';
import * as _DateTimeBox from './DateTimeBox';
import { ColumnFilter, ColumnFilterOperator, requiresArgument } from './DataGrid.Filter';
import { DataGridColumn } from './DataGrid.Column';
import Button from './Button';

function getPossibleOperators<T>(allPossibleFilters: ColumnFilter<T>[], propertyPath: string): ColumnFilterOperator[] {
	return allPossibleFilters
		.filter(x => x.propertyPath === propertyPath)
		.map(x => x.operator);
}

function formatPath<T>(path: string, columns: DataGridColumn<T>[]) {
	return columns.find(x => Binding.propertyPathToString(x.linkTo) === path)!.title;
}

function formatOperator(operator: ColumnFilterOperator): string {
	switch (operator) {
		case '==': return Res.WA.WA_DATAGRID_FILTER_EQUALS();
		case '!=': return Res.WA.WA_DATAGRID_FILTER_NOT_EQUALS();
		case '<': return Res.WA.WA_DATAGRID_FILTER_LESS();
		case '<=': return Res.WA.WA_DATAGRID_FILTER_LESS_OR_EQUALS();
		case '>': return Res.WA.WA_DATAGRID_FILTER_GREATER();
		case '>=': return Res.WA.WA_DATAGRID_FILTER_GREATER_OR_EQUALS();
		case 'empty': return Res.WA.WA_DATAGRID_FILTER_EMPTY();
		case 'non_empty': return Res.WA.WA_DATAGRID_FILTER_NON_EMPTY();
		case 'starts_with': return Res.WA.WA_DATAGRID_FILTER_STARTS_WITH();
		case 'contains': return Res.WA.WA_DATAGRID_FILTER_CONTAINS();
	}
}

export type CreateFilterControlParams<T> = {
	pathPrefix: string;
	filter: ColumnFilter<T>;
	possibleArguments: string[];
	allPossibleFilters: ColumnFilter<T>[];
	columns: DataGridColumn<T>[];
	onChange: () => void;
	onRemove: () => void;
};

export function createFilterControls<T>({
	pathPrefix,
	filter,
	allPossibleFilters,
	possibleArguments,
	columns,
	onChange,
	onRemove }: CreateFilterControlParams<T>) {

	const TextBox: _TextBox.TextBoxCtor<{ filters: ColumnFilter<T>[] }> = _TextBox.TextBox;
	const DecimalBox: _DecimalBox.DecimalBoxCtor<{ filters: ColumnFilter<T>[] }> = _DecimalBox.DecimalBox;
	const DateTimeBox: _DateTimeBox.DateTimeBoxCtor<{ filters: ColumnFilter<T>[] }> = _DateTimeBox.DateTimeBox;
	const ComboBox: _ComboBox.ComboBoxCtor<{ filters: ColumnFilter<T>[] }> = _ComboBox.ComboBox;

	// No puedo partir de Columns porque podría haber columnas sin filtros disponibles (por ejemplo por
	// estar completamente vacía), así que hago un distinct sobre los property paths de los filtros existentes
	const pathOptions = Object.keys(allPossibleFilters.reduce((acc, x) => { acc[x.propertyPath] = 1; return acc; }, {} as any));

	const operatorOptions = getPossibleOperators(allPossibleFilters, filter.propertyPath);

	const buildArgumentInput = () => {
		if (!requiresArgument(filter.operator)) {
			return null;
		}

		if (filter.dataType === 'number') {
			return <DecimalBox linkTo={`${pathPrefix}.argument`} precision={2} allowNegative format={Format.asNumber} required />;
		}

		if (filter.dataType === 'date' || filter.dataType === 'datetime') {
			const placeholder = filter.dataType === 'date' ? 'dd/mm/aaaa' : 'dd/mm/aaaa hh:mm';
			return <DateTimeBox linkTo={`${pathPrefix}.argument`} placeholder={placeholder} required />;
		}

		if (possibleArguments.length === 0 || filter.operator === 'contains' || filter.operator === 'starts_with') {
			return <TextBox linkTo={`${pathPrefix}.argument`} required />;
		}

		// Aseguro que los argumentos posibles contiene el valor actual de argument, por si es un valor
		// introducido a mano por el usuario y no uno de los valores preestablecidos
		if (filter.argument && !possibleArguments.includes(filter.argument as string))
			possibleArguments = [filter.argument as string, ...possibleArguments];

		return <ComboBox linkTo={`${pathPrefix}.argument`} options={possibleArguments} required />;
	};

	// Actualizo algunas partes a mano porque necesito refrescar controles antes del updateModel

	const updatePath = (newPaths: string[]) => {
		const sampleFilter = allPossibleFilters.find(x => x.propertyPath === newPaths[0]);

		// Copio los valores (formato, tipo, etc.) de otro filtro de esa columna al filtro que estoy editando
		Object.assign(filter, sampleFilter, { argument: '' });

		onChange();
	};

	const updateOperator = (newOperators: ColumnFilterOperator[]) => {
		filter.operator = newOperators[0];
		onChange();
	};

	return <React.Fragment key={pathPrefix}>
		<Row>
			<Column width='one-quarter'>
				<ComboBox linkTo={`${pathPrefix}.propertyPath`} options={pathOptions} format={x => formatPath(x, columns)} onValueChanged={updatePath} required />
			</Column>
			<Column width='one-quarter'>
				<ComboBox linkTo={`${pathPrefix}.operator`} options={operatorOptions} format={formatOperator} onValueChanged={updateOperator} required />
			</Column>
			<Column width='one-quarter'>
				{buildArgumentInput()}
			</Column>
			<Column width='one-quarter'>
				<Button onClick={() => onRemove()}>
					<i className='fa fa-minus text-danger'></i> {Res.Common.DELETE_BTN()}
				</Button>
			</Column>
		</Row>
	</React.Fragment>;
}
